/**
 * Factory for creating and registering built-in draggers.
 *
 * Extracts dragger registration logic from Facade to keep it focused on coordination.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DraggerManager } from "./DraggerManager";
import type { BlockDragger } from "./BlockDragger";
import type { TextDragger } from "./TextDragger";
import type { SelectDragger } from "./SelectDragger";
import type { ZoomWindowDragger } from "./ZoomWindowDragger";

import { DraggerType } from "./types";
import { FreehandDragger } from "./FreehandDragger";
import { LineDragger } from "./LineDragger";
import { RectangleDragger } from "./RectangleDragger";
import { EllipseDragger } from "./EllipseDragger";
import { DimensionDragger } from "./DimensionDragger";
import { PolylineDragger } from "./PolylineDragger";
import { BlockDragger as BlockDraggerClass } from "./BlockDragger";
import { TextDragger as TextDraggerClass } from "./TextDragger";
import { OriginDragger } from "./OriginDragger";
import { ZoomWindowDragger as ZoomWindowDraggerClass } from "./ZoomWindowDragger";
import { SelectDragger as SelectDraggerClass } from "./SelectDragger";
import { EraserDragger } from "./EraserDragger";
import { CopyDragger } from "./CopyDragger";
import { MoveDragger } from "./MoveDragger";
import { RotateDragger } from "./RotateDragger";
import { ScaleDragger } from "./ScaleDragger";
import { HandwritingDragger } from "./HandwritingDragger";
import { StylusKeyboardDragger } from "./StylusKeyboardDragger";

/**
 * References to draggers that callers may need to access.
 */
export interface DraggerReferences {
  blockDragger: BlockDragger;
  textDragger: TextDragger;
  handwritingDragger: HandwritingDragger;
  stylusKeyboardDragger: StylusKeyboardDragger;
  selectDragger: SelectDragger;
  zoomWindowDragger: ZoomWindowDragger;
}

/**
 * Context needed to create draggers.
 */
export interface DraggerFactoryContext {
  viewer: DRDrawingViewer;
}

/**
 * Register all built-in draggers with the manager.
 *
 * @param manager The DraggerManager to register draggers with
 * @param ctx Context with viewer and optional selectionSet
 * @returns References to draggers that may need to be accessed later
 */
export function registerBuiltinDraggers(
  manager: DraggerManager,
  ctx: DraggerFactoryContext,
): DraggerReferences {
  const { viewer } = ctx;

  // Register freehand dragger.
  // Pas d'auto-selection : on enchaine souvent plusieurs traits d'affilee
  // (a fortiori pour ecrire a la main), et voir le menu de selection surgir
  // apres chaque trait coupe le geste.
  const freehandDragger = new FreehandDragger(viewer, {
    autoSelect: false,
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Freehand, freehandDragger);

  // Register handwriting (draw2text) dragger
  const handwritingDragger = new HandwritingDragger(viewer, {
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Handwriting, handwritingDragger);

  // Register stylus-keyboard dragger (tap to place, device keyboard to write)
  const stylusKeyboardDragger = new StylusKeyboardDragger(viewer, {
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.StylusKeyboard, stylusKeyboardDragger);

  // Register OneWay draggers
  const lineDragger = new LineDragger(viewer);
  manager.register(DraggerType.Line, lineDragger);

  const rectangleDragger = new RectangleDragger(viewer, {
    snapOptions: {
      acceptObject: true,
      acceptOrtho: false,
      acceptPolar: true,
    },
  });
  manager.register(DraggerType.Rectangle, rectangleDragger);

  const ellipseDragger = new EllipseDragger(viewer, {
    snapOptions: {
      acceptObject: true,
      acceptOrtho: false,
      acceptPolar: true,
    },
  });
  manager.register(DraggerType.Ellipse, ellipseDragger);

  // Always placed point by point: a dimension measures between two precise
  // points, often snapped, which a single stroke can't give.
  const dimensionDragger = new DimensionDragger(viewer, {
    snapOptions: {
      acceptObject: true,
      acceptOrtho: true,
      acceptPolar: true,
    },
  });
  manager.register(DraggerType.Dimension, dimensionDragger);

  // Register sequential draggers
  const polylineDragger = new PolylineDragger(viewer, {
    autoSelect: true,
  });
  manager.register(DraggerType.Polyline, polylineDragger);

  // Register block dragger
  const blockDragger = new BlockDraggerClass(viewer, {
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Block, blockDragger);

  // Register text dragger
  const textDragger = new TextDraggerClass(viewer, {
    autoSelect: true,
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Text, textDragger);

  // Register origin dragger
  const originDragger = new OriginDragger(viewer, {
    snapOptions: {
      acceptObject: true,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Origin, originDragger);

  // Register zoom window dragger
  const zoomWindowDragger = new ZoomWindowDraggerClass(viewer, {
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.ZoomWindow, zoomWindowDragger);

  // Register select dragger (uses selectionService singleton internally)
  const selectDragger = new SelectDraggerClass(viewer, {
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Select, selectDragger);

  // Register the base-point edit tools (copy/move/rotate/scale). Each needs
  // the manager itself so it can switch back to the previous tool once the
  // user is done.
  const copyDragger = new CopyDragger(viewer, manager, {
    autoSelect: false,
    snapOptions: {
      acceptObject: true,
      acceptOrtho: true,
      acceptPolar: true,
    },
  });
  manager.register(DraggerType.Copy, copyDragger);

  const moveDragger = new MoveDragger(viewer, manager, {
    autoSelect: false,
    snapOptions: {
      acceptObject: true,
      acceptOrtho: true,
      acceptPolar: true,
    },
  });
  manager.register(DraggerType.Move, moveDragger);

  // Ortho is off for rotate: constraining the angle point to the horizontal or
  // vertical axis would only ever allow multiples of 90°. Polar is what gives
  // the useful increments here.
  const rotateDragger = new RotateDragger(viewer, manager, {
    autoSelect: false,
    snapOptions: {
      acceptObject: true,
      acceptOrtho: false,
      acceptPolar: true,
    },
  });
  manager.register(DraggerType.Rotate, rotateDragger);

  // Object snap only: the reference and target lengths are usually read off
  // existing geometry, and an ortho/polar constraint would silently change the
  // distance the factor is computed from.
  const scaleDragger = new ScaleDragger(viewer, manager, {
    autoSelect: false,
    snapOptions: {
      acceptObject: true,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Scale, scaleDragger);

  // Register eraser dragger
  const eraserDragger = new EraserDragger(viewer, {
    snapOptions: {
      acceptObject: false,
      acceptOrtho: false,
      acceptPolar: false,
    },
  });
  manager.register(DraggerType.Eraser, eraserDragger);

  return {
    blockDragger,
    textDragger,
    handwritingDragger,
    stylusKeyboardDragger,
    selectDragger,
    zoomWindowDragger,
  };
}
